import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Loader2, Send, MessageSquare, User, Truck } from "lucide-react";

export const TaskComments = ({ task, mode = "admin", onClose, onPosted }) => {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!task) return;
    try {
      const r = mode === "portal" ? await api.portalTaskComments(task.id) : await api.taskComments(task.id);
      setItems(r.items || []);
    } catch { toast.error("تعذّر تحميل التعليقات"); }
  }, [task, mode]);

  useEffect(() => { if (task) { setItems(null); setText(""); load(); } }, [task, load]);
  useEffect(() => { if (task?.id) (mode === "portal" ? api.portalTaskSeen : api.taskSeen)(task.id).then((r) => { if (r?.seen_by?.length !== (task.seen_by || []).length) onPosted?.(); }).catch(() => {}); }, [task?.id, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const fn = mode === "portal" ? api.addPortalTaskComment : api.addTaskComment;
      const c = await fn(task.id, t);
      setItems((arr) => [...(arr || []), c]);
      setText("");
      onPosted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-md max-h-[85vh] flex flex-col font-body" data-testid="task-comments-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-base flex items-center gap-1.5"><MessageSquare className="h-4 w-4 text-blue-600" /> تعليقات المهمة</DialogTitle>
          <DialogDescription className="truncate">{task?.title}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 py-1 min-h-[160px]">
          {!items ? (
            <div className="py-10 grid place-items-center text-slate-300"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">لا توجد تعليقات بعد — ابدأ المحادثة</div>
          ) : items.map((c) => (
            <div key={c.id} data-testid={`task-comment-${c.id}`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">
                {c.author_type === "employee" ? <Truck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                <span className="font-semibold text-slate-700">{c.author_name}</span>
                <span className="ms-auto font-mono-nums" dir="ltr">{String(c.created_at).slice(0, 16).replace("T", " ")}</span>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="flex gap-1.5 pt-2 border-t border-slate-100">
          <Input data-testid="task-comment-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="اكتب تعليقاً…" />
          <Button data-testid="task-comment-send" onClick={send} disabled={sending} className="shrink-0 gap-1.5">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
